// src/pages/BlogPost.js
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from '../services/firebase';
import { collection, getDocs, doc, getDoc, query, where, limit } from 'firebase/firestore';
import SEO from '../components/SEO';
import { useTranslation } from 'react-i18next';
import { pickDocField, pickCategoryLabel } from '../utils/i18nContent';

function toDate(value) {
  if (!value) return null;
  if (value.seconds) return new Date(value.seconds * 1000);
  if (typeof value === 'string') return new Date(value);
  if (value instanceof Date) return value;
  return null;
}

// Fonction pour formater le contenu avec mise en forme enrichie
const formatContent = (content) => {
  if (!content) return '';
  
  return content
    // Titres
    .replace(/^# (.*$)/gim, '<h1 class="content-h1">$1</h1>')
    .replace(/^## (.*$)/gim, '<h2 class="content-h2">$1</h2>')
    .replace(/^### (.*$)/gim, '<h3 class="content-h3">$1</h3>')
    .replace(/^#### (.*$)/gim, '<h4 class="content-h4">$1</h4>')
    
    // Gras
    .replace(/\*\*(.*?)\*\*/gim, '<strong class="content-bold">$1</strong>')
    .replace(/\*\*(.*?)\*\*/gim, '<strong class="content-bold">$1</strong>')
    
    // Italique
    .replace(/\*(.*?)\*/gim, '<em class="content-italic">$1</em>')
    .replace(/_(.*?)_/gim, '<em class="content-italic">$1</em>')
    
    // Barré
    .replace(/~~(.*?)~~/gim, '<del class="content-strikethrough">$1</del>')
    
    // Citations
    .replace(/^> (.*$)/gim, '<blockquote class="content-blockquote">$1</blockquote>')
    
    // Listes non ordonnées
    .replace(/^\s*[-*+] (.*$)/gim, '<ul class="content-ul"><li class="content-li">$1</li></ul>')
    .replace(/<\/ul>\s*<ul[^>]*>/gim, '')
    
    // Listes ordonnées
    .replace(/^\s*\d+\. (.*$)/gim, '<ol class="content-ol"><li class="content-li">$1</li></ol>')
    .replace(/<\/ol>\s*<ol[^>]*>/gim, '')
    
    // Liens
    .replace(/\[([^\[]+)\]\(([^\)]+)\)/gim, '<a href="$2" class="content-link" target="_blank" rel="noopener noreferrer">$1</a>')
    
    // Séparateurs
    .replace(/^\s*---\s*$/gim, '<hr class="content-hr">')
    
    // Paragraphes (gestion des sauts de ligne)
    .replace(/\n\n/g, '</p><p class="content-paragraph">')
    .replace(/\n/g, '<br>')
    
    // Encapsulation finale
    .replace(/^(.*)$/gim, '<p class="content-paragraph">$1</p>')
    
    // Nettoyage des balises vides
    .replace(/<p class="content-paragraph"><\/p>/g, '')
    .replace(/<p class="content-paragraph"><\/p>/g, '');
};

const BlogPost = () => {
  const { slugOrId } = useParams();
  const { t, i18n } = useTranslation('blog');
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [readingTime, setReadingTime] = useState('');
  const [formattedContent, setFormattedContent] = useState('');

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      try {
        let found = null;

        // 1) Par slug (filtré sur "published" pour respecter les règles)
        if (slugOrId && isNaN(Number(slugOrId))) {
          const q = query(
            collection(db, 'blog_posts'),
            where('slug', '==', slugOrId),
            where('status', '==', 'published'),
            limit(1)
          );
          const qs = await getDocs(q);
          if (!qs.empty) {
            const d = qs.docs[0];
            found = { id: d.id, ...d.data() };
          }
        }

        // 2) Fallback par id (et on vérifie aussi le statut)
        if (!found) {
          const ref = doc(db, 'blog_posts', slugOrId);
          const snap = await getDoc(ref);
          if (snap.exists()) {
            const data = snap.data();
            if (data.status === 'published') {
              found = { id: snap.id, ...data };
            } else {
              found = null;
            }
          }
        }

        setPost(found);

        // Temps de lecture et formatage du contenu
        if (found) {
          const content = pickDocField(found, 'content', i18n.language, 'fr') || '';
          const wordsPerMinute = 200;
          const words = content.trim().split(/\s+/).length;
          const time = Math.max(1, Math.ceil(words / wordsPerMinute));
          setReadingTime(
            time <= 1
              ? t('readingTime.oneMinute', { defaultValue: '1 min read' })
              : t('readingTime.minutes', { time, defaultValue: '{{time}} min read' })
          );
          
          // Formater le contenu
          setFormattedContent(formatContent(content));
        } else {
          setReadingTime('');
          setFormattedContent('');
        }
      } catch (e) {
        console.error('Erreur récupération article:', e);
        setPost(null);
        setFormattedContent('');
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slugOrId, i18n.language, t]);

  if (loading) {
    return (
      <div className="blogpost-loading">
        <div className="container">
          <div className="loading-spinner"></div>
          <p>{t('list.loading', { defaultValue: 'Loading articles...' })}</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="blogpost-not-found">
        <div className="container">
          <h2>{t('list.empty', { defaultValue: 'No articles available at the moment' })}</h2>
          <Link to="/blog" className="back-to-blog-btn">
            {t('backToList', { defaultValue: 'Back to blog' })}
          </Link>
        </div>
      </div>
    );
  }

  const title = pickDocField(post, 'title', i18n.language, 'fr') || '';
  const excerpt = pickDocField(post, 'excerpt', i18n.language, 'fr') || '';
  const category = pickCategoryLabel(post.category, i18n.language, 'fr');
  const cover = post.coverImage || '';
  const d = toDate(post.createdAt);
  const dateStr = d
    ? d.toLocaleDateString(i18n.language || 'fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  return (
    <div className="blogpost-container">
      <SEO
        title={`${title} - MKD Studio`}
        description={
          excerpt ||
          t('seo.description', {
            defaultValue: 'Read our articles on dance, MKD Studio news, and tips to improve.',
          })
        }
      />

      <div className="blogpost-hero">
        <div className="container">
          <Link to="/blog" className="back-link">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {t('backToList', { defaultValue: 'Back to blog' })}
          </Link>

          <div className="blogpost-meta-top">
            {category && <span className="category-badge">{category}</span>}
            {dateStr && <time className="publish-date">{dateStr}</time>}
            {readingTime && <span className="reading-time">{readingTime}</span>}
          </div>

          <h1 className="blogpost-title">{title}</h1>
          {excerpt && <p className="blogpost-excerpt">{excerpt}</p>}
        </div>
      </div>

      <div className="container">
        <article className="blogpost-article">
          {cover && (
            <div className="blogpost-cover-container">
              <img src={cover} alt={title} className="blogpost-cover-image" />
            </div>
          )}

          <section className="blogpost-content">
            <div className="content-wrapper">
              <div 
                className="blogpost-text formatted-content" 
                dangerouslySetInnerHTML={{ __html: formattedContent }} 
              />

              {post.tags && post.tags.length > 0 && (
                <div className="blogpost-tags">
                  <h4>{t('tags', { defaultValue: 'Tags' })}</h4>
                  <div className="tags-list">
                    {post.tags.map((tag, index) => (
                      <span key={index} className="tag">{tag}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="blogpost-share">
              <h4>{t('share', { defaultValue: 'Share this article' })}</h4>
              <div className="share-buttons">
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="share-btn twitter"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z"/></svg>
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="share-btn facebook"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg>
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="share-btn linkedin"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/><circle cx="4" cy="4" r="2"/></svg>
                </a>
              </div>
            </div>
          </section>
        </article>

        <div className="blogpost-navigation">
          <Link to="/blog" className="navigation-button">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {t('backToList', { defaultValue: 'Back to blog' })}
          </Link>
        </div>
      </div>

      <style jsx>{`
        .blogpost-container {
          padding: 0;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
          color: #333;
          line-height: 1.6;
        }
        
        .container {
          max-width: 800px;
          margin: 0 auto;
          padding: 0 1rem;
        }
        
        /* Hero section */
        .blogpost-hero {
          background: linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%);
          padding: 3rem 0 2rem;
          margin-bottom: 2rem;
        }
        
        .back-link {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 1.25rem;
          color: #5a4ae3;
          text-decoration: none;
          font-weight: 500;
          transition: color 0.2s ease;
          font-size: 0.95rem;
        }
        
        .back-link:hover {
          color: #4030c0;
        }
        
        .blogpost-meta-top {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1rem;
          flex-wrap: wrap;
        }
        
        .category-badge {
          background: #5a4ae3;
          color: white;
          padding: 0.4rem 0.8rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
        }
        
        .publish-date, .reading-time {
          color: #666;
          font-size: 0.85rem;
        }
        
        .blogpost-title {
          font-size: 2.25rem;
          font-weight: 800;
          line-height: 1.2;
          margin: 0 0 1rem;
          color: #1a1a1a;
        }
        
        .blogpost-excerpt {
          font-size: 1.1rem;
          color: #555;
          margin: 0;
          line-height: 1.5;
        }
        
        /* Image de couverture */
        .blogpost-cover-container {
          margin: 0 0 2.5rem;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
        }
        
        .blogpost-cover-image {
          width: 100%;
          height: auto;
          display: block;
        }
        
        /* Contenu formaté */
        .blogpost-content {
          margin-bottom: 3rem;
        }
        
        .content-wrapper {
          max-width: 700px;
          margin: 0 auto;
        }
        
        .formatted-content {
          font-size: 1.1rem;
          line-height: 1.8;
          color: #333;
        }
        
        .content-paragraph {
          margin-bottom: 1.5rem;
          text-align: justify;
        }
        
        .content-h1 {
          font-size: 2rem;
          font-weight: 800;
          margin: 2.5rem 0 1.5rem;
          color: #1a1a1a;
          line-height: 1.3;
          border-bottom: 3px solid #5a4ae3;
          padding-bottom: 0.5rem;
        }
        
        .content-h2 {
          font-size: 1.7rem;
          font-weight: 700;
          margin: 2.5rem 0 1.25rem;
          color: #1a1a1a;
          line-height: 1.3;
          border-left: 4px solid #5a4ae3;
          padding-left: 1rem;
        }
        
        .content-h3 {
          font-size: 1.4rem;
          font-weight: 600;
          margin: 2rem 0 1rem;
          color: #2a2a2a;
          line-height: 1.3;
        }
        
        .content-h4 {
          font-size: 1.2rem;
          font-weight: 600;
          margin: 1.5rem 0 0.75rem;
          color: #2a2a2a;
          line-height: 1.3;
        }
        
        .content-bold {
          font-weight: 700;
          color: #1a1a1a;
          background: linear-gradient(120deg, #5a4ae3 0%, #4030c0 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .content-italic {
          font-style: italic;
          color: #555;
        }
        
        .content-strikethrough {
          text-decoration: line-through;
          color: #888;
        }
        
        .content-blockquote {
          border-left: 4px solid #5a4ae3;
          padding: 1.5rem 2rem;
          margin: 2rem 0;
          background: #f8f9ff;
          border-radius: 0 8px 8px 0;
          font-style: italic;
          color: #555;
          position: relative;
        }
        
        .content-blockquote::before {
          content: '"';
          font-size: 3rem;
          color: #5a4ae3;
          position: absolute;
          left: 10px;
          top: -10px;
          opacity: 0.3;
        }
        
        .content-ul, .content-ol {
          margin: 1.5rem 0;
          padding-left: 2rem;
        }
        
        .content-li {
          margin-bottom: 0.5rem;
          line-height: 1.6;
        }
        
        .content-ul .content-li {
          list-style-type: disc;
        }
        
        .content-ol .content-li {
          list-style-type: decimal;
        }
        
        .content-link {
          color: #5a4ae3;
          text-decoration: none;
          font-weight: 600;
          border-bottom: 2px solid transparent;
          transition: all 0.2s ease;
        }
        
        .content-link:hover {
          color: #4030c0;
          border-bottom-color: #4030c0;
        }
        
        .content-hr {
          border: none;
          height: 2px;
          background: linear-gradient(90deg, transparent, #5a4ae3, transparent);
          margin: 2.5rem 0;
        }
        
        /* Tags */
        .blogpost-tags {
          margin: 2.5rem 0;
          padding-top: 2rem;
          border-top: 1px solid #eee;
        }
        
        .blogpost-tags h4 {
          margin: 0 0 0.75rem;
          font-size: 1.1rem;
          color: #555;
        }
        
        .tags-list {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }
        
        .tag {
          background: #f0f0ff;
          color: #5a4ae3;
          padding: 0.4rem 0.8rem;
          border-radius: 6px;
          font-size: 0.85rem;
          font-weight: 500;
        }
        
        /* Partage */
        .blogpost-share {
          border-top: 1px solid #eee;
          padding-top: 2rem;
          margin-top: 2rem;
        }
        
        .blogpost-share h4 {
          margin: 0 0 1rem;
          font-size: 1.1rem;
          color: #555;
        }
        
        .share-buttons {
          display: flex;
          gap: 0.75rem;
        }
        
        .share-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          color: white;
          text-decoration: none;
          transition: transform 0.2s ease;
        }
        
        .share-btn:hover {
          transform: translateY(-2px);
        }
        
        .share-btn.twitter {
          background: #1da1f2;
        }
        
        .share-btn.facebook {
          background: #3b5998;
        }
        
        .share-btn.linkedin {
          background: #0077b5;
        }
        
        /* Navigation */
        .blogpost-navigation {
          display: flex;
          justify-content: center;
          margin-bottom: 3rem;
        }
        
        .navigation-button {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: #5a4ae3;
          color: white;
          text-decoration: none;
          border-radius: 8px;
          font-weight: 500;
          transition: background 0.2s ease;
          font-size: 0.95rem;
        }
        
        .navigation-button:hover {
          background: #4030c0;
        }
        
        /* Loading state */
        .blogpost-loading {
          padding: 3rem 0;
          text-align: center;
        }
        
        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #5a4ae3;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1.25rem;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        /* Not found state */
        .blogpost-not-found {
          padding: 4rem 0;
          text-align: center;
        }
        
        .blogpost-not-found h2 {
          margin-bottom: 1.5rem;
          color: #666;
        }
        
        .back-to-blog-btn {
          display: inline-block;
          padding: 0.75rem 1.5rem;
          background: #5a4ae3;
          color: white;
          text-decoration: none;
          border-radius: 8px;
          font-weight: 500;
          transition: background 0.2s ease;
        }
        
        .back-to-blog-btn:hover {
          background: #4030c0;
        }
        
        /* Responsive Design */
        @media (max-width: 768px) {
          .container {
            padding: 0 1rem;
          }
          
          .blogpost-hero {
            padding: 2rem 0 1.5rem;
            margin-bottom: 1.5rem;
          }
          
          .blogpost-title {
            font-size: 1.75rem;
          }
          
          .blogpost-excerpt {
            font-size: 1rem;
          }
          
          .formatted-content {
            font-size: 1rem;
          }
          
          .content-h1 {
            font-size: 1.6rem;
            margin: 2rem 0 1.25rem;
          }
          
          .content-h2 {
            font-size: 1.4rem;
            margin: 2rem 0 1rem;
          }
          
          .content-h3 {
            font-size: 1.25rem;
            margin: 1.5rem 0 0.75rem;
          }
          
          .content-h4 {
            font-size: 1.1rem;
            margin: 1.25rem 0 0.5rem;
          }
          
          .blogpost-cover-container {
            margin: 0 0 1.5rem;
            border-radius: 8px;
          }
          
          .blogpost-meta-top {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
          }
          
          .blogpost-content {
            margin-bottom: 2rem;
          }
          
          .blogpost-tags {
            margin: 2rem 0;
          }
          
          .blogpost-share {
            padding-top: 1.5rem;
            margin-top: 1.5rem;
          }
          
          .blogpost-navigation {
            margin-bottom: 2rem;
          }
          
          .content-blockquote {
            padding: 1rem 1.5rem;
            margin: 1.5rem 0;
          }
          
          .content-ul, .content-ol {
            padding-left: 1.5rem;
          }
        }
        
        @media (max-width: 480px) {
          .blogpost-hero {
            padding: 1.5rem 0 1.25rem;
          }
          
          .blogpost-title {
            font-size: 1.5rem;
          }
          
          .back-link {
            font-size: 0.9rem;
          }
          
          .content-h1 {
            font-size: 1.4rem;
          }
          
          .content-h2 {
            font-size: 1.25rem;
          }
          
          .content-h3 {
            font-size: 1.15rem;
          }
          
          .content-blockquote {
            padding-left: 1rem;
            margin: 1.25rem 0;
          }
          
          .share-buttons {
            justify-content: center;
          }
          
          .navigation-button {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default BlogPost;